/**
 * Viewport utilities for configurable breakpoints
 */

/**
 * Migrate old data format to new viewports array
 * Call this when loading project data
 *
 * Handles two legacy formats:
 * 1. Main app format: { urls: { figmaDesktopUrl, figmaMobileUrl } }
 * 2. Plugin format: { desktopUrl, mobileUrl }
 */
export function migrateToViewports(projectData) {
  // Already migrated - check for non-empty viewports array
  if (projectData.viewports && Array.isArray(projectData.viewports) && projectData.viewports.length > 0) {
    return projectData;
  }

  // Handle main app format: urls.figmaDesktopUrl
  const desktopUrl = projectData.urls?.figmaDesktopUrl || null;
  const mobileUrl = projectData.urls?.figmaMobileUrl || null;

  // Handle plugin format: desktopUrl (no urls wrapper)
  const pluginDesktopUrl = projectData.desktopUrl || null;
  const pluginMobileUrl = projectData.mobileUrl || null;

  // Use whichever format exists
  const finalDesktop = desktopUrl || pluginDesktopUrl;
  const finalMobile = mobileUrl || pluginMobileUrl;

  return {
    ...projectData,
    viewports: [
      { name: "Mobile", minWidth: 0, figmaUrl: finalMobile || null },
      { name: "Desktop", minWidth: 768, figmaUrl: finalDesktop || null }
    ]
  };
}

/**
 * Get the active viewport based on window width
 */
export function getActiveViewport(viewports, windowWidth) {
  if (!viewports || viewports.length === 0) return null;
  const sorted = [...viewports].sort((a, b) => b.minWidth - a.minWidth);
  return sorted.find(v => windowWidth >= v.minWidth) || sorted[sorted.length - 1];
}

/**
 * Get the effective Figma URL for a viewport (with inheritance)
 * Returns empty string if no URL is found (never returns null)
 */
export function getEffectiveFigmaUrl(viewport, allViewports) {
  if (!viewport || !allViewports || allViewports.length === 0) return '';
  if (viewport.figmaUrl) return viewport.figmaUrl;

  // Sort ascending and find current index
  const sorted = [...allViewports].sort((a, b) => a.minWidth - b.minWidth);
  const currentIndex = sorted.findIndex(v => v.minWidth === viewport.minWidth);

  // Look for next larger viewport with URL
  for (let i = currentIndex + 1; i < sorted.length; i++) {
    if (sorted[i].figmaUrl) return sorted[i].figmaUrl;
  }

  // Fallback: look smaller
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (sorted[i].figmaUrl) return sorted[i].figmaUrl;
  }

  return '';
}

/**
 * Get smart icon based on pixel width
 */
export function getViewportIcon(minWidth) {
  if (minWidth < 768) return '📱';   // Mobile
  if (minWidth < 1440) return '💻';  // Tablet/Desktop (laptop)
  return '🖥️';                       // Large Desktop (monitor)
}

/**
 * Validate viewports array
 */
export function validateViewports(viewports) {
  const errors = [];

  if (!viewports || viewports.length < 2) {
    errors.push('Minimum 2 viewports required');
  }
  if (viewports && viewports.length > 6) {
    errors.push('Maximum 6 viewports allowed');
  }

  if (viewports && viewports.length > 0) {
    // Check for base viewport (0px)
    if (!viewports.some(v => v.minWidth === 0)) {
      errors.push('Must have a base viewport at 0px');
    }

    // Check for duplicate widths
    const widths = viewports.map(v => v.minWidth);
    const duplicates = widths.filter((w, i) => widths.indexOf(w) !== i);
    if (duplicates.length > 0) {
      errors.push('Breakpoint values must be unique');
    }

    // Check for duplicate names
    const names = viewports.map(v => v.name.toLowerCase());
    const dupNames = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupNames.length > 0) {
      errors.push('Viewport names must be unique');
    }

    // Check at least one URL exists
    if (!viewports.some(v => v.figmaUrl)) {
      errors.push('At least one viewport must have a Figma URL');
    }

    // Check minWidth range
    if (viewports.some(v => v.minWidth < 0 || v.minWidth > 3840)) {
      errors.push('Breakpoint values must be between 0 and 3840');
    }
  }

  return errors;
}

/**
 * Get suggested name and width for new viewport
 */
export function suggestNewViewport(existingViewports) {
  if (!existingViewports || existingViewports.length === 0) {
    return { minWidth: 768, name: 'Desktop' };
  }

  const sorted = [...existingViewports].sort((a, b) => a.minWidth - b.minWidth);
  const existingWidths = sorted.map(v => v.minWidth);
  const largestWidth = existingWidths[existingWidths.length - 1];

  // Suggest next logical breakpoint
  const suggestions = [
    { width: 768, name: 'Tablet' },
    { width: 1024, name: 'Desktop' },
    { width: 1440, name: 'Large Desktop' },
    { width: 1920, name: '4K' },
  ];

  for (const s of suggestions) {
    if (!existingWidths.includes(s.width) && s.width > largestWidth) {
      return { minWidth: s.width, name: s.name };
    }
  }

  // Fallback
  return { minWidth: largestWidth + 200, name: `Viewport ${existingViewports.length + 1}` };
}

/**
 * Find the viewport that a given viewport inherits from
 */
export function getInheritingFrom(viewport, allViewports) {
  if (!viewport || !allViewports || viewport.figmaUrl) return null;

  // Sort ascending and find current index
  const sorted = [...allViewports].sort((a, b) => a.minWidth - b.minWidth);
  const currentIndex = sorted.findIndex(v => v.minWidth === viewport.minWidth);

  // Look for next larger viewport with URL
  for (let i = currentIndex + 1; i < sorted.length; i++) {
    if (sorted[i].figmaUrl) return sorted[i].name;
  }

  // Fallback: look smaller
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (sorted[i].figmaUrl) return sorted[i].name;
  }

  return null;
}

/**
 * True when a URL is a Figma file/design link rather than an embeddable
 * prototype link. Shared by the viewport editor (inline warning) and the
 * Aero save guard so both reject the same inputs. Empty is not "wrong".
 */
export function isWrongUrlType(url) {
  if (!url) return false;
  return url.includes('figma.com/file') || url.includes('figma.com/design');
}

/**
 * Get default viewports for a new project
 */
export function getDefaultViewports() {
  return [
    { name: "Mobile", minWidth: 0, figmaUrl: null },
    { name: "Desktop", minWidth: 768, figmaUrl: null }
  ];
}

/**
 * Convert viewports array back to legacy format for backward compatibility
 * This ensures old consumers can still read the data
 */
export function viewportsToLegacyFormat(viewports) {
  if (!viewports || viewports.length === 0) {
    return { figmaDesktopUrl: '', figmaMobileUrl: '' };
  }

  const sorted = [...viewports].sort((a, b) => a.minWidth - b.minWidth);

  // Mobile is the smallest (0px)
  const mobileViewport = sorted.find(v => v.minWidth === 0);
  // Desktop is the largest non-mobile
  const desktopViewport = sorted.filter(v => v.minWidth > 0).pop();

  const mobileUrl = mobileViewport ? getEffectiveFigmaUrl(mobileViewport, viewports) : null;
  const desktopUrl = desktopViewport ? getEffectiveFigmaUrl(desktopViewport, viewports) : null;

  return {
    figmaDesktopUrl: desktopUrl || '',
    figmaMobileUrl: mobileUrl || ''
  };
}
